import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from '../../plugins/axios';

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

export interface LeadProduct {
    id: number;
    name: string;
    level1: string;
    level2?: string;
    level3?: string;
    questions?: unknown[];
}

// Answers are keyed by question identifier (`Level1`, `Level2`, `Level3`,
// `ReferrerEmail`, `Division`, `AccountAuto`, `ProductId`, plus per-question keys).
// Values vary by question type, so the map is loosely typed.
export type LeadFormAnswers = Record<string, any> & {
    ReferrerEmail?: any;
    Division?: any;
};

interface LeadFormState {
    fetchingLeadProducts: boolean;
    leadProducts: LeadProduct[];
    answers: LeadFormAnswers;
}

const initialState: LeadFormState = {
    fetchingLeadProducts: false,
    leadProducts: [],
    answers: {},
};

export const fetchLeadProducts = createAsyncThunk<LeadProduct[]>(
    'leadForm/fetchLeadProducts',
    async () => {
        const response = await axios.get(`${apiUrl}/leadproducts`);
        return response.data;
    },
);

export const leadFormSlice = createSlice({
    name: 'leadForm',
    initialState,
    reducers: {
        setAnswer(state, action: PayloadAction<{ key: string; answer: unknown }>) {
            state.answers = {
                ...state.answers,
                [action.payload.key]: action.payload.answer,
            };
        },
        clearAnswers(state) {
            state.answers = {
                ReferrerEmail: state.answers.ReferrerEmail,
                Division: state.answers.Division,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLeadProducts.pending, (state) => {
                state.fetchingLeadProducts = true;
            })
            .addCase(fetchLeadProducts.fulfilled, (state, action: PayloadAction<LeadProduct[]>) => {
                state.leadProducts = action.payload;
                state.fetchingLeadProducts = false;
            })
            .addCase(fetchLeadProducts.rejected, (state) => {
                state.fetchingLeadProducts = false;
            });
    },
});

export const { setAnswer, clearAnswers } = leadFormSlice.actions;
export default leadFormSlice.reducer;
